/**
 * Ticket Filters Component
 *
 * Filter bar for filtering tickets by type, status, and priority.
 * Used in ticket list pages.
 */

import React from 'react';
import { Filter as FilterIcon } from 'lucide-react';
import { Select } from '../common/Select';
import { TicketType, TicketStatus, TicketPriority } from '../../types/support';

export interface TicketFiltersProps {
  /** Current type filter */
  type?: TicketType | 'all';

  /** Current status filter */
  status?: TicketStatus | 'all';

  /** Current priority filter */
  priority?: TicketPriority | 'all';

  /** Type change handler */
  onTypeChange?: (type: TicketType | 'all') => void;

  /** Status change handler */
  onStatusChange?: (status: TicketStatus | 'all') => void;

  /** Priority change handler */
  onPriorityChange?: (priority: TicketPriority | 'all') => void;

  /** Show type filter */
  showType?: boolean;

  /** Show status filter */
  showStatus?: boolean;

  /** Show priority filter */
  showPriority?: boolean;
}

export const TicketFilters: React.FC<TicketFiltersProps> = ({
  type = 'all',
  status = 'all',
  priority = 'all',
  onTypeChange,
  onStatusChange,
  onPriorityChange,
  showType = true,
  showStatus = true,
  showPriority = true,
}) => {
  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: TicketType.GENERAL_INQUIRY, label: 'General Inquiry' },
    { value: TicketType.APPLICATION_SUPPORT, label: 'Application' },
    { value: TicketType.AUDIT_QUESTION, label: 'Audit' },
    { value: TicketType.TRAINING_SUPPORT, label: 'Training' },
    { value: TicketType.PAYMENT_ISSUE, label: 'Payment' },
    { value: TicketType.CERTIFICATE_REQUEST, label: 'Certificate' },
    { value: TicketType.RENEWAL_SUPPORT, label: 'Renewal' },
    { value: TicketType.TECHNICAL_ISSUE, label: 'Technical' },
    { value: TicketType.COMPLAINT, label: 'Complaint' },
    { value: TicketType.FEEDBACK, label: 'Feedback' },
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: TicketStatus.OPEN, label: 'Open' },
    { value: TicketStatus.IN_PROGRESS, label: 'In Progress' },
    { value: TicketStatus.WAITING_CUSTOMER, label: 'Waiting for Customer' },
    { value: TicketStatus.WAITING_INTERNAL, label: 'Waiting for Team' },
    { value: TicketStatus.RESOLVED, label: 'Resolved' },
    { value: TicketStatus.CLOSED, label: 'Closed' },
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: TicketPriority.LOW, label: 'Low' },
    { value: TicketPriority.MEDIUM, label: 'Medium' },
    { value: TicketPriority.HIGH, label: 'High' },
    { value: TicketPriority.URGENT, label: 'Urgent' },
  ];

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2 text-neutral-600">
        <FilterIcon className="w-5 h-5" />
        <span className="text-sm font-medium">Filters:</span>
      </div>

      {showType && onTypeChange && (
        <Select
          value={type}
          onChange={(event) => onTypeChange(event.target.value as unknown as TicketType | 'all')}
          className="min-w-[180px]"
        >
          {typeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      )}

      {showStatus && onStatusChange && (
        <Select
          value={status}
          onChange={(event) => onStatusChange(event.target.value as unknown as TicketStatus | 'all')}
          className="min-w-[180px]"
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      )}

      {showPriority && onPriorityChange && (
        <Select
          value={priority}
          onChange={(event) => onPriorityChange(event.target.value as unknown as TicketPriority | 'all')}
          className="min-w-[150px]"
        >
          {priorityOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      )}
    </div>
  );
};
